import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  UserPlus, Clock, Stethoscope, CheckCircle, Users, AlertTriangle, 
  Activity, Calendar, RefreshCw, Play, Eye, Filter, Search, Maximize2, Minimize2, X,
  Download, FileText, FileSpreadsheet, Plus, MessageCircle, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PatientSearchDropdown } from '@/components/SearchDropdowns';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { downloadCSV, downloadExcel, EXPORT_COLUMNS } from '@/lib/exportUtils';

export default function VisitDashboard() {
  const navigate = useNavigate();
  const [activeVisits, setActiveVisits] = useState([]);
  const [queue, setQueue] = useState({ queue: [], stats: {} });
  const [stats, setStats] = useState({});
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('queue');
  const [patientSearch, setPatientSearch] = useState('');
  const [visitSearch, setVisitSearch] = useState('');
  const [isMaximized, setIsMaximized] = useState(false);

  // Advanced filter states
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [visitTypeFilter, setVisitTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [checkInForm, setCheckInForm] = useState({
    patient_id: '',
    visit_type: 'OPD',
    assigned_doctor_id: '',
    department: '',
    chief_complaint: '',
    priority: 'normal',
    notes: '',
    consultation_fee: '',
    payment_status: 'unpaid',
    referring_doctor_id: ''
  });

  // Referring doctors
  const [referringDoctors, setReferringDoctors] = useState([]);
  const [showQuickAddDoctor, setShowQuickAddDoctor] = useState(false);
  const [quickAddForm, setQuickAddForm] = useState({ full_name: '', phone_number: '', specialty: '' });
  const [pendingNotification, setPendingNotification] = useState(null);

  const departments = ['General Medicine', 'Cardiology', 'Orthopedics', 'Pediatrics', 'Gynecology', 'ENT', 'Dermatology', 'Emergency'];

  // Advanced filtering for active visits
  const filteredActiveVisits = useMemo(() => {
    let filtered = activeVisits;
    
    // Text search
    if (visitSearch.trim()) {
      const query = visitSearch.toLowerCase();
      filtered = filtered.filter(visit => 
        visit.visit_number?.toLowerCase().includes(query) ||
        visit.id?.toLowerCase().includes(query) ||
        visit.patient?.full_name?.toLowerCase().includes(query) ||
        visit.patient?.patient_number?.toLowerCase().includes(query) ||
        visit.chief_complaint?.toLowerCase().includes(query)
      );
    }
    
    // Doctor filter
    if (doctorFilter && doctorFilter !== 'all') {
      filtered = filtered.filter(visit => visit.assigned_doctor_id === doctorFilter);
    }
    
    // Status filter
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(visit => visit.status === statusFilter);
    }
    
    // Visit type filter
    if (visitTypeFilter && visitTypeFilter !== 'all') {
      filtered = filtered.filter(visit => visit.visit_type === visitTypeFilter);
    }
    
    // Priority filter
    if (priorityFilter && priorityFilter !== 'all') {
      filtered = filtered.filter(visit => visit.priority === priorityFilter);
    }
    
    // Department filter
    if (departmentFilter && departmentFilter !== 'all') {
      filtered = filtered.filter(visit => visit.department === departmentFilter);
    }
    
    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(visit => {
        const visitDate = new Date(visit.created_at).toISOString().split('T')[0];
        return visitDate >= dateFrom;
      });
    }
    if (dateTo) {
      filtered = filtered.filter(visit => {
        const visitDate = new Date(visit.created_at).toISOString().split('T')[0];
        return visitDate <= dateTo;
      });
    }
    
    return filtered;
  }, [activeVisits, visitSearch, doctorFilter, statusFilter, visitTypeFilter, priorityFilter, departmentFilter, dateFrom, dateTo]);

  // Advanced filtering for queue
  const filteredQueue = useMemo(() => {
    let filtered = queue.queue || [];
    
    // Text search
    if (visitSearch.trim()) {
      const query = visitSearch.toLowerCase();
      filtered = filtered.filter(visit => 
        visit.visit_number?.toLowerCase().includes(query) ||
        visit.id?.toLowerCase().includes(query) ||
        visit.patient?.full_name?.toLowerCase().includes(query) ||
        visit.patient?.patient_number?.toLowerCase().includes(query) ||
        visit.chief_complaint?.toLowerCase().includes(query)
      );
    }
    
    // Doctor filter
    if (doctorFilter && doctorFilter !== 'all') {
      filtered = filtered.filter(visit => visit.assigned_doctor_id === doctorFilter);
    }
    
    // Visit type filter
    if (visitTypeFilter && visitTypeFilter !== 'all') {
      filtered = filtered.filter(visit => visit.visit_type === visitTypeFilter);
    }
    
    // Priority filter
    if (priorityFilter && priorityFilter !== 'all') {
      filtered = filtered.filter(visit => visit.priority === priorityFilter);
    }
    
    // Department filter
    if (departmentFilter && departmentFilter !== 'all') {
      filtered = filtered.filter(visit => visit.department === departmentFilter);
    }
    
    return filtered;
  }, [queue.queue, visitSearch, doctorFilter, visitTypeFilter, priorityFilter, departmentFilter]);

  // Clear all filters
  const clearFilters = () => {
    setVisitSearch('');
    setDoctorFilter('all');
    setStatusFilter('all');
    setVisitTypeFilter('all');
    setPriorityFilter('all');
    setDepartmentFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = visitSearch || doctorFilter !== 'all' || statusFilter !== 'all' || 
    visitTypeFilter !== 'all' || priorityFilter !== 'all' || departmentFilter !== 'all' || dateFrom || dateTo;

  // Export handlers
  const handleExportCSV = () => {
    const dataToExport = activeTab === 'queue' ? filteredQueue : filteredActiveVisits;
    if (dataToExport.length === 0) {
      toast.error('No data to export');
      return;
    }
    // Enrich data with doctor names
    const enrichedData = dataToExport.map(visit => ({
      ...visit,
      assigned_doctor_name: doctors.find(d => d.id === visit.assigned_doctor_id)?.full_name || 'N/A'
    }));
    downloadCSV(enrichedData, EXPORT_COLUMNS.visits, `visits_${activeTab}_export`);
    toast.success(`Exported ${dataToExport.length} visit(s) to CSV`);
  };

  const handleExportExcel = () => {
    const dataToExport = activeTab === 'queue' ? filteredQueue : filteredActiveVisits;
    if (dataToExport.length === 0) {
      toast.error('No data to export');
      return;
    }
    const enrichedData = dataToExport.map(visit => ({
      ...visit,
      assigned_doctor_name: doctors.find(d => d.id === visit.assigned_doctor_id)?.full_name || 'N/A'
    }));
    downloadExcel(enrichedData, EXPORT_COLUMNS.visits, `visits_${activeTab}_export`);
    toast.success(`Exported ${dataToExport.length} visit(s) to Excel`);
  };

  useEffect(() => {
    fetchData();
    fetchReferringDoctors();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [activeRes, queueRes, statsRes, patientsRes, doctorsRes] = await Promise.all([
        api.get('/visits/active'),
        api.get('/visits/queue'),
        api.get('/visits/stats'),
        api.get('/patients'),
        api.get('/users/doctors')
      ]);
      setActiveVisits(activeRes.data);
      setQueue(queueRes.data);
      setStats(statsRes.data);
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferringDoctors = async () => {
    try {
      const response = await api.get('/referring-doctors?status=active');
      setReferringDoctors(response.data);
    } catch (error) {
      console.error('Failed to fetch referring doctors:', error);
    }
  };

  const handleQuickAddDoctor = async () => {
    if (!quickAddForm.full_name || !quickAddForm.phone_number) {
      toast.error('Name and phone number are required');
      return;
    }
    try {
      const response = await api.post('/referring-doctors', {
        full_name: quickAddForm.full_name,
        phone_number: quickAddForm.phone_number,
        specialty: quickAddForm.specialty || 'General Practice',
        status: 'active'
      });
      toast.success('Referring doctor added');
      setReferringDoctors([...referringDoctors, response.data]);
      setCheckInForm({ ...checkInForm, referring_doctor_id: response.data.id });
      setShowQuickAddDoctor(false);
      setQuickAddForm({ full_name: '', phone_number: '', specialty: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add doctor');
    }
  };

  const confirmReferralNotification = async (visitId) => {
    try {
      await api.post(`/visits/${visitId}/confirm-referral-notification`);
      toast.success('Thank you message sent to referring doctor!');
      setPendingNotification(null);
    } catch (error) {
      toast.error('Failed to send notification');
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    if (!checkInForm.patient_id || !checkInForm.chief_complaint) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      // Create visit
      const visitData = {
        patient_id: checkInForm.patient_id,
        visit_type: checkInForm.visit_type,
        assigned_doctor_id: checkInForm.assigned_doctor_id,
        department: checkInForm.department,
        chief_complaint: checkInForm.chief_complaint,
        priority: checkInForm.priority,
        notes: checkInForm.notes,
        referring_doctor_id: checkInForm.referring_doctor_id || null
      };
      
      const response = await api.post('/visits', visitData);
      const visit = response.data;
      
      // Check if we need to show referral notification confirmation
      if (visit.pending_whatsapp_notification && visit.referring_doctor_name) {
        setPendingNotification({
          visitId: visit.id,
          doctorName: visit.referring_doctor_name
        });
      }
      
      // Create billing entry if consultation fee is provided
      if (checkInForm.consultation_fee && parseFloat(checkInForm.consultation_fee) > 0) {
        const consultationFee = parseFloat(checkInForm.consultation_fee);
        const isPaid = checkInForm.payment_status !== 'unpaid';
        
        const billingData = {
          patient_id: checkInForm.patient_id,
          visit_id: visit.id,
          items: [
            {
              description: 'Consultation Fee',
              amount: consultationFee,
              quantity: 1
            }
          ],
          notes: `Payment: ${checkInForm.payment_status.replace('paid_', 'Paid - ').replace('unpaid', 'Unpaid').replace('insurance', 'Insurance')}`
        };
        
        try {
          const billingResponse = await api.post('/billing', billingData);
          
          // If paid, record the payment
          if (isPaid && billingResponse.data?.id) {
            const paymentMethod = checkInForm.payment_status === 'paid_cash' ? 'cash' : 
                                  checkInForm.payment_status === 'paid_card' ? 'card' : 
                                  checkInForm.payment_status === 'insurance' ? 'insurance' : 'cash';
            
            await api.post(`/billing/${billingResponse.data.id}/payment`, {
              amount: consultationFee,
              payment_method: paymentMethod
            });
          }
        } catch (billingError) {
          console.error('Billing creation failed:', billingError);
          // Don't fail the check-in if billing fails
          toast.warning('Patient checked in but billing entry could not be created');
        }
      }
      
      toast.success(`Patient checked in! Token: ${visit.token_number || 'N/A'}`);
      setCheckInDialogOpen(false);
      setPatientSearch('');
      setCheckInForm({
        patient_id: '',
        visit_type: 'OPD',
        assigned_doctor_id: '',
        department: '',
        chief_complaint: '',
        priority: 'normal',
        notes: '',
        consultation_fee: '',
        payment_status: 'unpaid'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in patient');
    }
  };

  const handleStartConsultation = async (visitId) => {
    try {
      await api.put(`/visits/${visitId}/start-consultation`);
      toast.success('Consultation started');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start consultation');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'emergency': return 'bg-rose-100 text-rose-700 border-rose-200';
      case 'urgent': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getVisitTypeColor = (type) => {
    switch (type) {
      case 'OPD': return 'bg-purple-200 text-blue-700';
      case 'IPD': return 'bg-purple-100 text-purple-700';
      case 'Emergency': return 'bg-rose-100 text-rose-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatWaitTime = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="visit-dashboard">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Visit Management
            </h1>
            <p className="text-slate-600 mt-2">Track and manage patient visits in real-time</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Dialog open={checkInDialogOpen} onOpenChange={(open) => {
              setCheckInDialogOpen(open);
              if (!open) setIsMaximized(false);
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Check-In Patient
                </Button>
              </DialogTrigger>
              <DialogContent 
                className={`overflow-y-auto transition-all duration-200 ${
                  isMaximized 
                    ? '!fixed !inset-2 !left-2 !top-2 !right-2 !bottom-2 !translate-x-0 !translate-y-0 !max-w-none !w-[calc(100vw-16px)] !h-[calc(100vh-16px)] !max-h-none' 
                    : 'max-w-lg max-h-[85vh]'
                }`}
              >
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle>Patient Check-In</DialogTitle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsMaximized(!isMaximized)}
                      className="h-8 w-8 p-0 hover:bg-slate-100"
                      title={isMaximized ? "Minimize" : "Maximize"}
                    >
                      {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </DialogHeader>
                <form onSubmit={handleCheckIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Patient *</Label>
                    <PatientSearchDropdown
                      patients={patients}
                      selectedPatientId={checkInForm.patient_id}
                      onSelect={(patientId) => setCheckInForm({...checkInForm, patient_id: patientId})}
                      placeholder="Search patient by name, ID, or phone..."
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Visit Type *</Label>
                      <Select value={checkInForm.visit_type} onValueChange={(v) => setCheckInForm({...checkInForm, visit_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OPD">OPD</SelectItem>
                          <SelectItem value="IPD">IPD</SelectItem>
                          <SelectItem value="Emergency">Emergency</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={checkInForm.priority} onValueChange={(v) => setCheckInForm({...checkInForm, priority: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="emergency">Emergency</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select value={checkInForm.department} onValueChange={(v) => setCheckInForm({...checkInForm, department: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Assign Doctor</Label>
                    <Select value={checkInForm.assigned_doctor_id} onValueChange={(v) => setCheckInForm({...checkInForm, assigned_doctor_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select doctor (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {doctors.map(d => (
                          <SelectItem key={d.id} value={d.id}>
                            Dr. {d.full_name} {d.specialization && `(${d.specialization})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Referring Doctor Section */}
                  <div className="space-y-2">
                    <Label className="flex items-center justify-between">
                      <span>Referring Doctor</span>
                      <button
                        type="button"
                        onClick={() => setShowQuickAddDoctor(true)}
                        className="text-xs text-[#4A238F] hover:underline flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" /> Quick Add
                      </button>
                    </Label>
                    <Select 
                      value={checkInForm.referring_doctor_id || "none"} 
                      onValueChange={(v) => setCheckInForm({...checkInForm, referring_doctor_id: v === "none" ? "" : v})}
                    >
                      <SelectTrigger data-testid="referring-doctor-select">
                        <SelectValue placeholder="Select referring doctor (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {referringDoctors.map(d => (
                          <SelectItem key={d.id} value={d.id}>
                            Dr. {d.full_name} {d.specialty && `(${d.specialty})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {checkInForm.referring_doctor_id && (
                      <p className="text-xs text-emerald-600 flex items-center gap-1">
                        <MessageCircle className="h-3 w-3" /> 
                        WhatsApp thank you will be sent after check-in
                      </p>
                    )}
                  </div>

                  {/* Billing Details Section */}
                  <div className="border-t border-b border-slate-200 py-4 my-2">
                    <Label className="text-sm font-semibold text-slate-700 mb-3 block">Billing Details</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-slate-500">Consultation Fee</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={checkInForm.consultation_fee}
                            onChange={(e) => setCheckInForm({...checkInForm, consultation_fee: e.target.value})}
                            placeholder="0.00"
                            className="pl-7"
                            data-testid="consultation-fee-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-slate-500">Payment Status</Label>
                        <Select value={checkInForm.payment_status} onValueChange={(v) => setCheckInForm({...checkInForm, payment_status: v})}>
                          <SelectTrigger data-testid="payment-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unpaid">Unpaid</SelectItem>
                            <SelectItem value="paid_cash">Paid - Cash</SelectItem>
                            <SelectItem value="paid_card">Paid - Card</SelectItem>
                            <SelectItem value="insurance">Insurance</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Chief Complaint *</Label>
                    <Input
                      value={checkInForm.chief_complaint}
                      onChange={(e) => setCheckInForm({...checkInForm, chief_complaint: e.target.value})}
                      placeholder="Reason for visit"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <textarea
                      value={checkInForm.notes}
                      onChange={(e) => setCheckInForm({...checkInForm, notes: e.target.value})}
                      className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
                      placeholder="Additional notes..."
                    />
                  </div>

                  <Button type="submit" className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Check-In Patient
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="border border-blue-200 bg-purple-100">
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 text-purple-700 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-700">{stats.today?.total || 0}</p>
              <p className="text-xs text-purple-700">Today&apos;s Visits</p>
            </CardContent>
          </Card>
          <Card className="border border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-700">{stats.current?.waiting || 0}</p>
              <p className="text-xs text-amber-600">Waiting</p>
            </CardContent>
          </Card>
          <Card className="border border-purple-200 bg-indigo-50">
            <CardContent className="p-4 text-center">
              <Stethoscope className="h-6 w-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-700">{stats.current?.in_consultation || 0}</p>
              <p className="text-xs text-purple-600">In Consultation</p>
            </CardContent>
          </Card>
          <Card className="border border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-700">{stats.completed_today || 0}</p>
              <p className="text-xs text-emerald-600">Completed</p>
            </CardContent>
          </Card>
          <Card className="border border-sky-200 bg-sky-50">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-sky-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-sky-700">{stats.today?.opd || 0}</p>
              <p className="text-xs text-sky-600">OPD</p>
            </CardContent>
          </Card>
          <Card className="border border-rose-200 bg-rose-50">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="h-6 w-6 text-rose-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-rose-700">{stats.today?.emergency || 0}</p>
              <p className="text-xs text-rose-600">Emergency</p>
            </CardContent>
          </Card>
        </div>

        {/* Queue Stats */}
        {queue.stats && (
          <Card className="mb-6 border border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Clock className="h-5 w-5 text-amber-600" />
                  <span className="font-medium text-amber-800">Queue Status:</span>
                  <Badge className="bg-amber-100 text-amber-700">{queue.stats.total_waiting || 0} waiting</Badge>
                  <span className="text-sm text-amber-700">Avg wait: {Math.round(queue.stats.average_wait_minutes || 0)} min</span>
                </div>
                <div className="flex gap-2">
                  {queue.stats.priority_breakdown?.emergency > 0 && (
                    <Badge className="bg-rose-100 text-rose-700">{queue.stats.priority_breakdown.emergency} Emergency</Badge>
                  )}
                  {queue.stats.priority_breakdown?.urgent > 0 && (
                    <Badge className="bg-amber-100 text-amber-700">{queue.stats.priority_breakdown.urgent} Urgent</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search and Filters */}
        <Card className="mb-6 border border-slate-200 bg-white">
          <CardContent className="p-4">
            {/* Search Box */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by Patient Name, Patient ID, or Visit ID..."
                value={visitSearch}
                onChange={(e) => setVisitSearch(e.target.value)}
                className="pl-10 pr-10"
                data-testid="visit-search-input"
              />
              {visitSearch && (
                <button 
                  onClick={() => setVisitSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter Row */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600 font-medium">Filters:</span>
              </div>

              {/* Doctor Filter */}
              <Select value={doctorFilter} onValueChange={setDoctorFilter}>
                <SelectTrigger className="w-[180px]" data-testid="doctor-filter">
                  <SelectValue placeholder="Doctor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doctors</SelectItem>
                  {doctors.map((doctor) => (
                    <SelectItem key={doctor.id} value={doctor.id}>
                      Dr. {doctor.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Visit Type Filter */}
              <Select value={visitTypeFilter} onValueChange={setVisitTypeFilter}>
                <SelectTrigger className="w-[130px]" data-testid="visit-type-filter">
                  <SelectValue placeholder="Visit Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="OPD">OPD</SelectItem>
                  <SelectItem value="IPD">IPD</SelectItem>
                  <SelectItem value="Emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>

              {/* Priority Filter */}
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[130px]" data-testid="priority-filter">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>

              {/* Department Filter */}
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-[160px]" data-testid="department-filter">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Date From */}
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-slate-400" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[140px]"
                  data-testid="date-from-filter"
                />
              </div>

              {/* Date To */}
              <div className="flex items-center gap-1">
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[140px]"
                  data-testid="date-to-filter"
                />
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              )}

              {/* Export Buttons */}
              <div className="ml-auto flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={handleExportCSV}>
                      <FileText className="mr-2 h-4 w-4" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportExcel}>
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Export as Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Results count */}
            <div className="mt-3 text-sm text-slate-500">
              Showing {filteredActiveVisits.length} active visit(s), {filteredQueue.length} in queue
              {hasActiveFilters && ' (filtered)'}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-100 p-1.5 rounded-xl border border-slate-200 h-auto">
            <TabsTrigger 
              value="queue"
              className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all"
            >
              <Users className="mr-2 h-4 w-4" />
              OPD Queue ({filteredQueue.length})
            </TabsTrigger>
            <TabsTrigger 
              value="active"
              className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all"
            >
              <Activity className="mr-2 h-4 w-4" />
              Active Visits ({filteredActiveVisits.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="queue">
            {filteredQueue.length === 0 ? (
              <Card className="border border-slate-200 bg-white">
                <CardContent className="p-12 text-center">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">
                    {visitSearch ? `No patients matching "${visitSearch}"` : 'No patients in queue'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredQueue.map((visit, index) => (
                  <motion.div
                    key={visit.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <Card 
                      className={`border shadow-sm bg-white hover:shadow-md transition-all cursor-pointer ${visit.priority === 'emergency' ? 'border-l-4 border-l-rose-500' : visit.priority === 'urgent' ? 'border-l-4 border-l-amber-500' : ''}`}
                      onClick={() => navigate(`/visits/${visit.id}`)}
                      data-testid={`queue-card-${visit.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center font-bold text-amber-700 text-lg">
                              {visit.token_number || '-'}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-900">{visit.patient?.full_name || 'Unknown'}</h3>
                                <Badge className={getPriorityColor(visit.priority)}>{visit.priority}</Badge>
                              </div>
                              <p className="text-sm text-slate-600">{visit.chief_complaint}</p>
                              <p className="text-xs text-slate-500">
                                {visit.visit_number} • {visit.department} • Waiting: {formatWaitTime(visit.wait_time_minutes)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              onClick={() => handleStartConsultation(visit.id)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              <Play className="mr-1 h-4 w-4" />
                              Start
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/visits/${visit.id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="active">
            {filteredActiveVisits.length === 0 ? (
              <Card className="border border-slate-200 bg-white">
                <CardContent className="p-12 text-center">
                  <Activity className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">
                    {visitSearch ? `No visits matching "${visitSearch}"` : 'No active visits'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredActiveVisits.map((visit, index) => (
                  <motion.div
                    key={visit.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <Card 
                      className="border shadow-sm bg-white hover:shadow-md transition-all cursor-pointer"
                      onClick={() => navigate(`/visits/${visit.id}`)}
                      data-testid={`active-visit-card-${visit.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                              visit.status === 'waiting' ? 'bg-amber-100' : 'bg-purple-100'
                            }`}>
                              {visit.status === 'waiting' ? (
                                <Clock className="h-6 w-6 text-amber-600" />
                              ) : (
                                <Stethoscope className="h-6 w-6 text-purple-600" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-900">{visit.patient?.full_name || 'Unknown'}</h3>
                                <Badge className={getVisitTypeColor(visit.visit_type)}>{visit.visit_type}</Badge>
                                <Badge className={visit.status === 'waiting' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}>
                                  {visit.status === 'in_consultation' ? 'In Consultation' : visit.status}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600">{visit.chief_complaint}</p>
                              <p className="text-xs text-slate-500">
                                {visit.visit_number} • {visit.patient?.patient_number} • {visit.doctor ? `Dr. ${visit.doctor.full_name}` : 'Unassigned'}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/visits/${visit.id}`);
                            }}
                          >
                            <Eye className="mr-1 h-4 w-4" />
                            View
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Quick Add Referring Doctor Dialog */}
      <Dialog open={showQuickAddDoctor} onOpenChange={setShowQuickAddDoctor}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-[#4A238F]" />
              Quick Add Referring Doctor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={quickAddForm.full_name}
                onChange={(e) => setQuickAddForm({...quickAddForm, full_name: e.target.value})}
                placeholder="Dr. John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number (WhatsApp) *</Label>
              <Input
                value={quickAddForm.phone_number}
                onChange={(e) => setQuickAddForm({...quickAddForm, phone_number: e.target.value})}
                placeholder="+91 9876543210"
              />
            </div>
            <div className="space-y-2">
              <Label>Specialty</Label>
              <Select value={quickAddForm.specialty} onValueChange={(v) => setQuickAddForm({...quickAddForm, specialty: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select specialty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General Practice">General Practice</SelectItem>
                  <SelectItem value="Cardiology">Cardiology</SelectItem>
                  <SelectItem value="Orthopedics">Orthopedics</SelectItem>
                  <SelectItem value="Pediatrics">Pediatrics</SelectItem>
                  <SelectItem value="Gynecology">Gynecology</SelectItem>
                  <SelectItem value="ENT">ENT</SelectItem>
                  <SelectItem value="Dermatology">Dermatology</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickAddDoctor(false)}>
              Cancel
            </Button>
            <Button onClick={handleQuickAddDoctor} className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
              <Plus className="h-4 w-4 mr-2" />
              Add Doctor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Referral Notification Confirmation Dialog */}
      <Dialog open={!!pendingNotification} onOpenChange={() => setPendingNotification(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-600" />
              Send Thank You Message?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Patient has been checked in successfully. Would you like to send a &quot;Thank You&quot; WhatsApp message to{' '}
              <span className="font-semibold text-[#4A238F]">Dr. {pendingNotification?.doctorName}</span>?
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPendingNotification(null)}>
              Skip
            </Button>
            <Button 
              onClick={() => confirmReferralNotification(pendingNotification?.visitId)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Send className="h-4 w-4 mr-2" />
              Send WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
